/**
 * OnboardingWizard — Google Workspace tenant onboarding flow (W1 + W2 from
 * docs/mocks/ou-compliance-redesign.html).
 *
 * Four steps, tracked by `?step=` query param:
 *   1. welcome   — "Connect <Workspace> to Automatrix" — identify
 *   2. authorize — client_id + scope list copy fields + open Admin Console
 *   3. verify    — live probe polling per scope group
 *   4. complete  — first-audit kickoff message
 *
 * Non-admin branch: if the signed-in user appears NOT to be the Super Admin
 * for their tenant (no domain match, or explicit "I'm not an admin" toggle),
 * surface a "forward this to your IT admin" page with the same SA client_id
 * + scope list and copy buttons, plus a paste-blob the IT admin can follow.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
  Launch as LaunchIcon,
  HourglassEmpty as HourglassIcon,
} from '@mui/icons-material';

const TOKENS = {
  bg: '#f4f4f1',
  paper: '#ffffff',
  ink: '#131418',
  ink2: '#2a2d35',
  inkMute: '#6b6f78',
  inkFade: '#a4a8b1',
  line: '#e7e6e1',
  hairline: '#dcdad3',
  ok: '#1f8a4a',
  okSoft: '#d4f1de',
  warn: '#c48415',
  warnSoft: '#fde9b4',
  bad: '#d8351e',
  badSoft: '#fcddd1',
  info: '#2d72c4',
  accent: '#c93b1d',
  accent2: '#9f3010',
  sans: "'Instrument Sans', system-ui, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const step = params.get('step') || 'welcome';
  const isItForward = params.get('mode') === 'it-forward';

  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // Pre-fill identity from /onboarding/status (if user has a tenant in
  // 'unverified' state we recover their fields).
  const [identity, setIdentity] = useState({ loading: true });
  useEffect(() => {
    if (!token) return;
    fetch('/api/google-ou-compliance/onboarding/status', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setIdentity({ loading: false, data: d });
        // If verified, kick out
        if (d.status === 'verified') {
          navigate('/compliance/google-ou');
        }
      })
      .catch((e) =>
        setIdentity({ loading: false, error: e.message || String(e) }),
      );
  }, [token, navigate]);

  // DWD status — we use this for the SA client_id / scope list copy fields.
  const [dwd, setDwd] = useState(null);
  useEffect(() => {
    if (!token) return;
    fetch('/api/google-ou-compliance/dwd-status', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then(setDwd)
      .catch(() => {});
  }, [token]);

  // Wizard form state
  const [form, setForm] = useState({
    customerId: '',
    displayName: '',
    workspaceDomain: '',
    superAdminEmail: '',
  });
  useEffect(() => {
    if (identity.data?.tenant) {
      setForm({
        customerId: identity.data.tenant.customerId || '',
        displayName: identity.data.tenant.displayName || '',
        workspaceDomain: identity.data.tenant.workspaceDomain || '',
        superAdminEmail: identity.data.tenant.superAdminEmail || '',
      });
    }
  }, [identity.data]);

  const setStep = useCallback(
    (s) => {
      const next = new URLSearchParams(location.search);
      next.set('step', s);
      navigate(`/onboarding/google-workspace?${next.toString()}`);
    },
    [navigate, location.search],
  );

  if (identity.loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: TOKENS.bg,
        minHeight: '100vh',
        fontFamily: TOKENS.sans,
        color: TOKENS.ink,
        py: 6,
        px: 4,
      }}
    >
      <Box sx={{ maxWidth: 920, mx: 'auto' }}>
        <Stepper active={step} />

        <Paper
          sx={{
            mt: 4,
            borderRadius: 1,
            border: `1px solid ${TOKENS.hairline}`,
            boxShadow: 'none',
            background: TOKENS.paper,
            overflow: 'hidden',
          }}
        >
          {step === 'welcome' && (
            <WelcomeStep
              form={form}
              setForm={setForm}
              onNext={() => setStep('authorize')}
              isItForward={isItForward}
              onToggleItForward={() => {
                const next = new URLSearchParams(location.search);
                if (isItForward) next.delete('mode');
                else next.set('mode', 'it-forward');
                navigate(
                  `/onboarding/google-workspace?${next.toString()}`,
                );
              }}
            />
          )}
          {step === 'authorize' && (
            <AuthorizeStep
              dwd={dwd}
              form={form}
              isItForward={isItForward}
              onBack={() => setStep('welcome')}
              onNext={() => setStep('verify')}
            />
          )}
          {step === 'verify' && (
            <VerifyStep
              token={token}
              form={form}
              onBack={() => setStep('authorize')}
              onNext={() => setStep('complete')}
            />
          )}
          {step === 'complete' && (
            <CompleteStep
              token={token}
              form={form}
              identity={identity.data}
              onFinish={() => navigate('/compliance/google-ou')}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
}

// ── Stepper ──────────────────────────────────────────────────────────────

function Stepper({ active }) {
  const steps = [
    { id: 'welcome', label: 'Identify', sub: 'workspace' },
    { id: 'authorize', label: 'Authorize', sub: 'in Admin Console' },
    { id: 'verify', label: 'Verify', sub: 'live probes' },
    { id: 'complete', label: 'Done', sub: 'first audit runs' },
  ];
  const activeIdx = steps.findIndex((s) => s.id === active);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 0,
        background: TOKENS.paper,
        border: `1px solid ${TOKENS.hairline}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {steps.map((s, i) => {
        const isDone = i < activeIdx;
        const isActive = i === activeIdx;
        const tone = isActive
          ? TOKENS.accent
          : isDone
            ? TOKENS.ok
            : TOKENS.inkFade;
        return (
          <Box
            key={s.id}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderRight: i < steps.length - 1 ? `1px solid ${TOKENS.hairline}` : 'none',
              background: isActive ? '#fff' : 'transparent',
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: `1.5px solid ${tone}`,
                color: tone,
                display: 'grid',
                placeItems: 'center',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: TOKENS.mono,
              }}
            >
              {isDone ? '✓' : i + 1}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: tone }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: TOKENS.inkMute }}>
                {s.sub}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Welcome step ─────────────────────────────────────────────────────────

function WelcomeStep({ form, setForm, onNext, isItForward, onToggleItForward }) {
  const filled =
    form.customerId.trim().length > 0 &&
    form.displayName.trim().length > 0 &&
    form.superAdminEmail.trim().length > 0;

  return (
    <Box sx={{ p: 5 }}>
      <Typography
        sx={{
          fontFamily: TOKENS.sans,
          fontWeight: 700,
          fontSize: 30,
          letterSpacing: '-0.025em',
          mb: 1,
        }}
      >
        Connect your{' '}
        <Box component="span" sx={{ color: TOKENS.accent }}>
          Workspace
        </Box>{' '}
        to Automatrix
      </Typography>
      <Typography sx={{ color: TOKENS.inkMute, fontSize: 15, mb: 3, maxWidth: 720 }}>
        One-time setup, ~5 minutes. Your Workspace Super Admin authorizes our
        service account to read your configuration. Read-only — we never
        write policies, never see learner data, never store anything
        outside your tenant boundary.
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 540 }}>
        <TextField
          label="Workspace display name"
          placeholder="e.g. WDOC.gov"
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          fullWidth
          size="small"
        />
        <TextField
          label="Google customer id"
          placeholder="e.g. C01e8t2b7"
          value={form.customerId}
          onChange={(e) => setForm({ ...form, customerId: e.target.value })}
          fullWidth
          size="small"
          helperText="From Admin Console → Account → Account settings → Customer ID"
        />
        <TextField
          label="Primary domain (optional)"
          placeholder="e.g. wdoc.gov"
          value={form.workspaceDomain}
          onChange={(e) => setForm({ ...form, workspaceDomain: e.target.value })}
          fullWidth
          size="small"
        />
        <TextField
          label="Super Admin email (we impersonate this user, read-only)"
          placeholder="e.g. admin@wdoc.gov"
          value={form.superAdminEmail}
          onChange={(e) => setForm({ ...form, superAdminEmail: e.target.value })}
          fullWidth
          size="small"
        />
      </Stack>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Box
            component="span"
            onClick={onToggleItForward}
            sx={{
              fontSize: 12,
              color: TOKENS.accent,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {isItForward
              ? '← I am the Super Admin'
              : 'Not an admin? Forward this to IT →'}
          </Box>
        </Box>
        <Button
          variant="contained"
          disabled={!filled}
          onClick={onNext}
          sx={{ background: TOKENS.ink, '&:hover': { background: TOKENS.accent } }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
}

// ── Authorize step ───────────────────────────────────────────────────────

function AuthorizeStep({ dwd, form, onBack, onNext, isItForward }) {
  const saClientId = dwd?.saClientId || 'Loading…';
  const scopes = dwd?.scopes || [];
  const scopesBlob = scopes.join(',\n');

  const copy = useCallback((text) => {
    try {
      navigator.clipboard?.writeText(text);
    } catch {}
  }, []);

  const itPasteBlob = useMemo(() => {
    return `Hi — please add the following to our Google Workspace Admin Console under
Security → API controls → Domain-wide delegation, then reply "done" so we can verify access.

Service account client ID:
${saClientId}

OAuth scopes (comma-separated, all read-only):
${scopesBlob}

This is a one-time, read-only setup for Automatrix's OU compliance auditor.
`;
  }, [saClientId, scopesBlob]);

  return (
    <Box sx={{ p: 5 }}>
      <Typography
        sx={{
          fontFamily: TOKENS.sans,
          fontWeight: 700,
          fontSize: 30,
          letterSpacing: '-0.025em',
          mb: 1,
        }}
      >
        Add Automatrix to{' '}
        <Box component="span" sx={{ color: TOKENS.accent }}>
          Domain-Wide Delegation
        </Box>
      </Typography>
      <Typography sx={{ color: TOKENS.inkMute, fontSize: 14, mb: 3, maxWidth: 720 }}>
        In your Google Workspace Admin Console, paste the service account's
        client ID and the scope list below. Both fields have a copy button so
        this should take under a minute.
      </Typography>

      <FieldBlock label="Service account client ID" onCopy={() => copy(saClientId)}>
        <Box
          sx={{
            fontFamily: TOKENS.mono,
            fontSize: 14,
            color: TOKENS.ink,
            p: 1.5,
            background: TOKENS.bg,
            border: `1px solid ${TOKENS.hairline}`,
            borderRadius: 0.5,
            wordBreak: 'break-all',
          }}
        >
          {saClientId}
        </Box>
      </FieldBlock>

      <FieldBlock
        label="OAuth scopes (comma-separated, all read-only)"
        onCopy={() => copy(scopes.join(','))}
      >
        <Box
          sx={{
            fontFamily: TOKENS.mono,
            fontSize: 12,
            color: TOKENS.ink,
            p: 1.5,
            background: TOKENS.bg,
            border: `1px solid ${TOKENS.hairline}`,
            borderRadius: 0.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {scopesBlob}
        </Box>
      </FieldBlock>

      <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
        <strong>Why apps.licensing has no .readonly variant:</strong> Google's
        Enterprise License Manager API publishes one scope (no .readonly
        variant). We only ever call list verbs — never write, modify, or
        assign licenses. Verifiable in
        <code> policy-snapshot.service.ts → fetchLicenseAssignments</code>.
      </Alert>

      {isItForward && (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Email-ready paste block (for your IT admin)
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                fontFamily: TOKENS.mono,
                fontSize: 12,
                color: TOKENS.ink,
                p: 2,
                pr: 6,
                background: TOKENS.bg,
                border: `1px solid ${TOKENS.hairline}`,
                borderRadius: 0.5,
                whiteSpace: 'pre-wrap',
              }}
            >
              {itPasteBlob}
            </Box>
            <IconButton
              size="small"
              onClick={() => copy(itPasteBlob)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                background: TOKENS.paper,
              }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          startIcon={<LaunchIcon />}
          variant="outlined"
          onClick={() =>
            window.open(
              'https://admin.google.com/ac/owl/domainwidedelegation',
              '_blank',
              'noopener',
            )
          }
        >
          Open Admin Console DWD page
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onBack}>Back</Button>
        <Button
          variant="contained"
          onClick={onNext}
          sx={{ background: TOKENS.ink, '&:hover': { background: TOKENS.accent } }}
        >
          I've added them — verify access
        </Button>
      </Stack>
    </Box>
  );
}

function FieldBlock({ label, children, onCopy }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: TOKENS.inkMute, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: TOKENS.mono }}>
          {label}
        </Typography>
        <Button size="small" startIcon={<CopyIcon />} onClick={onCopy}>
          Copy
        </Button>
      </Box>
      {children}
    </Box>
  );
}

// ── Verify step (live probes) ────────────────────────────────────────────

function VerifyStep({ token, form, onBack, onNext }) {
  const [probes, setProbes] = useState(null);
  const [probing, setProbing] = useState(false);
  const [error, setError] = useState(null);

  const runProbe = useCallback(async () => {
    setProbing(true);
    setError(null);
    try {
      const resp = await fetch(
        '/api/google-ou-compliance/onboarding/probe',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerId: form.customerId.trim() || undefined,
            superAdminEmail: form.superAdminEmail.trim() || undefined,
          }),
        },
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || `HTTP ${resp.status}`);
      setProbes(data);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setProbing(false);
    }
  }, [token, form]);

  // Auto-run on mount
  useEffect(() => {
    runProbe();
  }, [runProbe]);

  const passed = probes?.probes?.filter((p) => p.status === 'pass').length || 0;
  const total = probes?.probes?.length || 0;
  const pct = total ? Math.round((passed / total) * 100) : 0;

  return (
    <Box sx={{ p: 5 }}>
      <Typography
        sx={{
          fontFamily: TOKENS.sans,
          fontWeight: 700,
          fontSize: 30,
          letterSpacing: '-0.025em',
          mb: 1,
        }}
      >
        Probing your{' '}
        <Box component="span" sx={{ color: TOKENS.accent }}>
          Workspace
        </Box>
        …
      </Typography>
      <Typography sx={{ color: TOKENS.inkMute, fontSize: 14, mb: 3, maxWidth: 720 }}>
        Running one read-only request per scope group. We don't fetch any
        data — we just confirm each scope was authorized. Normally 5–15
        seconds. Sometimes longer if DWD propagation is delayed — re-test if
        anything fails.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {probing && <LinearProgress sx={{ mb: 2 }} />}

      {probes && (
        <Box sx={{ border: `1px solid ${TOKENS.hairline}`, borderRadius: 1, overflow: 'hidden' }}>
          {probes.probes.map((p, i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: '32px 1fr 80px 100px',
                gap: 1.5,
                alignItems: 'center',
                p: 1.5,
                borderBottom: i < probes.probes.length - 1 ? `1px solid ${TOKENS.line}` : 'none',
                background: p.status === 'pass' ? '#fafff8' : '#fff5f3',
              }}
            >
              {p.status === 'pass' ? (
                <CheckIcon sx={{ color: TOKENS.ok, fontSize: 22 }} />
              ) : (
                <ErrorIcon sx={{ color: TOKENS.bad, fontSize: 22 }} />
              )}
              <Box>
                <Typography sx={{ fontFamily: TOKENS.mono, fontSize: 13, fontWeight: 500 }}>
                  {p.label}
                </Typography>
                {p.error && (
                  <Typography sx={{ fontSize: 11, color: TOKENS.bad, mt: 0.5 }}>
                    {p.error}
                  </Typography>
                )}
              </Box>
              <Typography
                sx={{ fontFamily: TOKENS.mono, fontSize: 12, color: TOKENS.inkMute, textAlign: 'right' }}
              >
                {p.latencyMs} ms
              </Typography>
              <Chip
                size="small"
                label={p.status === 'pass' ? 'Pass' : 'Fail'}
                sx={{
                  fontFamily: TOKENS.mono,
                  fontSize: 11,
                  background: p.status === 'pass' ? TOKENS.okSoft : TOKENS.badSoft,
                  color: p.status === 'pass' ? TOKENS.ok : TOKENS.bad,
                }}
              />
            </Box>
          ))}
          <Box
            sx={{
              p: 1.5,
              background: TOKENS.bg,
              borderTop: `1px solid ${TOKENS.line}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              sx={{ fontFamily: TOKENS.mono, fontSize: 18, fontWeight: 600 }}
            >
              {pct}%
            </Typography>
            <Typography sx={{ fontSize: 13, color: TOKENS.inkMute }}>
              {passed} of {total} probes passed
              {probes.allPassed
                ? ' — proceed to finish setup'
                : ' — re-test after fixing scopes'}
            </Typography>
          </Box>
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button onClick={onBack}>Back</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={runProbe} disabled={probing}>
          Re-test
        </Button>
        <Button
          variant="contained"
          disabled={!probes?.allPassed}
          onClick={onNext}
          sx={{ background: TOKENS.ink, '&:hover': { background: TOKENS.accent } }}
        >
          Continue
        </Button>
      </Stack>
    </Box>
  );
}

// ── Complete step ────────────────────────────────────────────────────────

function CompleteStep({ token, form, identity, onFinish }) {
  const [state, setState] = useState({ saving: false, error: null, done: false });

  // Persist the tenant on first render of this step.
  useEffect(() => {
    if (!token) return;
    if (state.saving || state.done) return;
    setState((s) => ({ ...s, saving: true }));
    fetch('/api/google-ou-compliance/onboarding/complete', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerId: form.customerId.trim(),
        displayName: form.displayName.trim(),
        workspaceDomain: form.workspaceDomain.trim() || undefined,
        superAdminEmail: form.superAdminEmail.trim(),
      }),
    })
      .then((r) =>
        r.json().then((d) => (r.ok ? d : Promise.reject(new Error(d.message || `HTTP ${r.status}`)))),
      )
      .then(() => setState({ saving: false, done: true, error: null }))
      .catch((e) =>
        setState({ saving: false, done: false, error: e.message || String(e) }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ p: 5, textAlign: 'center' }}>
      {state.saving && (
        <>
          <CircularProgress />
          <Typography sx={{ mt: 2, color: TOKENS.inkMute }}>
            Saving tenant…
          </Typography>
        </>
      )}
      {state.error && (
        <Alert severity="error" sx={{ textAlign: 'left' }}>
          Could not save tenant: {state.error}
        </Alert>
      )}
      {state.done && (
        <>
          <CheckIcon sx={{ fontSize: 64, color: TOKENS.ok }} />
          <Typography
            sx={{
              fontFamily: TOKENS.sans,
              fontWeight: 700,
              fontSize: 30,
              letterSpacing: '-0.025em',
              mt: 1,
              mb: 1,
            }}
          >
            <Box component="span" sx={{ color: TOKENS.accent }}>
              {form.displayName}
            </Box>{' '}
            is connected
          </Typography>
          <Typography sx={{ color: TOKENS.inkMute, fontSize: 14, mb: 3, maxWidth: 540, mx: 'auto' }}>
            We've saved your tenant. Click below to open the dashboard — your
            first audit will begin automatically.
          </Typography>
          <Button
            variant="contained"
            onClick={onFinish}
            sx={{ background: TOKENS.ink, '&:hover': { background: TOKENS.accent } }}
          >
            Open OU Compliance dashboard
          </Button>
        </>
      )}
    </Box>
  );
}
